import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import { Link } from "react-router-dom";
import {
  HIDDEN_KEYS,
  filterVisible,
  hideItem,
} from "../utils/hiddenHistory";
import {
  formatAppDate,
  todayLocalDateString,
} from "../utils/dateFormat";

const defaultSalary = () => ({
  employee_name: "",
  amount: "",
  salary_date: todayLocalDateString(),
  salary_target: "",
  notes: "",
});

export default function Salary() {
  const [salaries, setSalaries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [salary, setSalary] = useState(defaultSalary);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetAmount, setTargetAmount] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [salaryTargets, setSalaryTargets] = useState({});
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    fetchSalaries();
    loadDrivers();
    loadSalaryTargets();
  }, []);

  const formatNumber = (value) => Number(value ?? 0).toFixed(2);

  const loadSalaryTargets = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("salaryTargets") || "{}");
      setSalaryTargets(
        stored && typeof stored === "object" && !Array.isArray(stored)
          ? stored
          : {},
      );
    } catch (error) {
      setSalaryTargets({});
    }
  };

  const saveSalaryTarget = (employeeName, amount) => {
    const nextTargets = { ...salaryTargets };
    if (amount == null) {
      delete nextTargets[employeeName];
    } else {
      nextTargets[employeeName] = Number(amount);
    }
    localStorage.setItem("salaryTargets", JSON.stringify(nextTargets));
    setSalaryTargets(nextTargets);
  };

  const openSalaryTargetModal = () => {
    if (!filterEmployee) {
      alert("Select an employee first to set a salary target.");
      return;
    }
    setTargetAmount(String(salaryTargets[filterEmployee] ?? ""));
    setShowTargetModal(true);
  };

  const saveSalaryTargetForm = (e) => {
    e.preventDefault();
    if (!filterEmployee) {
      return;
    }
    if (targetAmount.trim() === "") {
      saveSalaryTarget(filterEmployee, null);
      setShowTargetModal(false);
      return;
    }
    const amount = Number(targetAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      alert("Enter a valid total salary amount.");
      return;
    }
    saveSalaryTarget(filterEmployee, amount);
    setShowTargetModal(false);
  };

  const fetchSalaries = async () => {
    setFetchError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/salaries`);
      setSalaries(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching salaries", error);
      setSalaries([]);
      setFetchError(
        error.response?.status === 500
          ? "Salaries table may be missing. Run the SQL to create the salaries table in your database."
          : "Could not load salaries. Check that the API is running.",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadDrivers = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("lagerDrivers") || "[]");
      setDrivers(Array.isArray(stored) ? stored : []);
    } catch (error) {
      setDrivers([]);
    }
  };

  const employeeNames = [
    ...new Set(
      [
        ...drivers.map((item) => item.name).filter(Boolean),
        ...salaries.map((s) => s.employee_name).filter(Boolean),
      ].map((name) => String(name).trim()),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const filteredSalaries = filterVisible(
    filterEmployee
      ? salaries.filter((s) => s.employee_name === filterEmployee)
      : salaries,
    HIDDEN_KEYS.salary,
  );

  const paymentsByEmployee = salaries.reduce((acc, item) => {
    const name = item.employee_name?.trim();
    if (!name) return acc;
    acc[name] = (acc[name] ?? 0) + Number(item.amount ?? 0);
    return acc;
  }, {});

  const remainingByEmployee = Object.entries(salaryTargets).reduce(
    (acc, [employeeName, target]) => {
      const remaining = Math.max(
        0,
        Number(target) - (paymentsByEmployee[employeeName] ?? 0),
      );
      acc[employeeName] = remaining;
      return acc;
    },
    {},
  );

  const totalRemaining = Object.values(remainingByEmployee).reduce(
    (sum, amount) => sum + amount,
    0,
  );

  const currentEmployeeTarget =
    filterEmployee && salaryTargets[filterEmployee] != null
      ? Number(salaryTargets[filterEmployee])
      : null;

  const currentEmployeePaid = filterEmployee
    ? filteredSalaries.reduce((sum, item) => sum + Number(item.amount ?? 0), 0)
    : 0;

  const currentEmployeeRemaining =
    currentEmployeeTarget != null
      ? Math.max(0, currentEmployeeTarget - currentEmployeePaid)
      : null;

  void listVersion;

  const totalAmount = filteredSalaries.reduce(
    (sum, item) => sum + Number(item.amount ?? 0),
    0,
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSalary({ ...salary, [name]: value });
  };

  const closeModal = () => {
    setSalary(defaultSalary());
    setShowModal(false);
  };

  const deleteSalary = (id) => {
    if (!window.confirm("Remove this salary from the list? Restore it from History."))
      return;
    hideItem(HIDDEN_KEYS.salary, id);
    setListVersion((v) => v + 1);
  };

  const saveSalary = async (e) => {
    e.preventDefault();
    const name = salary.employee_name?.trim();
    if (!name) {
      alert("Enter employee name");
      return;
    }
    const amount = Number(salary.amount);
    if (!Number.isFinite(amount) || amount < 0.01) {
      alert("Enter an amount of at least 0.01");
      return;
    }
    const target = Number(salary.salary_target);
    if (
      salary.salary_target !== "" &&
      Number.isFinite(target) &&
      target >= 0
    ) {
      saveSalaryTarget(name, target);
    }

    const payload = {
      employee_name: name,
      amount,
      salary_date: salary.salary_date,
      notes: salary.notes?.trim() || null,
    };
    try {
      await axios.post(`${API_BASE_URL}/api/salaries`, payload);
      alert("Salary recorded successfully");
      closeModal();
      fetchSalaries();
    } catch (error) {
      console.error("Error recording salary", error);
      const data = error.response?.data;
      const validationMsg = data?.errors
        ? Object.values(data.errors).flat().join(" ")
        : null;
      alert(
        validationMsg ||
          data?.message ||
          "Failed to record salary. Create the salaries table in your database.",
      );
    }
  };

  if (loading) {
    return <Loader message="Loading salary" fullPage />;
  }

  return (
    <>
      <div className="container-fluid app-container">
        {fetchError ? (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        ) : null}
        <div className="row">
          <div className="col-12">
            <div className="card app-screen-card">
              <div className="app-sales-toolbar">
                <button
                  type="button"
                  className="btn btn-primary app-btn"
                  onClick={() => setShowModal(true)}
                >
                  + Add Salary
                </button>
                <Link to="/history" className="btn btn-outline-secondary app-btn ms-2">
                  History
                </Link>
                <div className="app-sales-filter">
                  <div className="mb-0">
                    <label className="form-label mb-1">
                      Filter by employee
                    </label>
                    <select
                      className="form-select"
                      value={filterEmployee}
                      onChange={(e) => setFilterEmployee(e.target.value)}
                    >
                      <option value="">All employees</option>
                      {employeeNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary app-btn ms-2"
                  onClick={openSalaryTargetModal}
                >
                  Set target
                </button>
                <div className="app-sales-stats small">
                  <strong>Entries:</strong> {filteredSalaries.length}
                  <span className="app-sales-stats__sep"> | </span>
                  <strong>Total:</strong> {formatNumber(totalAmount)}
                  {filterEmployee ? (
                    <>
                      <span className="app-sales-stats__sep"> | </span>
                      <strong>Target:</strong>{" "}
                      {currentEmployeeTarget != null
                        ? formatNumber(currentEmployeeTarget)
                        : "—"}
                      <span className="app-sales-stats__sep"> | </span>
                      <strong>Remaining:</strong>{" "}
                      {currentEmployeeRemaining != null
                        ? formatNumber(currentEmployeeRemaining)
                        : "Set target"}
                    </>
                  ) : (
                    <>
                      <span className="app-sales-stats__sep"> | </span>
                      <strong>Total remaining:</strong>{" "}
                      {formatNumber(totalRemaining)}
                    </>
                  )}
                </div>
              </div>
              <div className="table-responsive app-table-wrap">
                <table className="table table-sm app-data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Employee</th>
                      <th>Amount</th>
                      <th>Notes</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSalaries.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No salary records yet.
                        </td>
                      </tr>
                    ) : (
                      filteredSalaries.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDate(item.salary_date)}
                          </td>
                          <td data-label="Employee">{item.employee_name}</td>
                          <td data-label="Amount" className="cell-highlight">
                            {formatNumber(item.amount)}
                          </td>
                          <td data-label="Notes">{item.notes || "-"}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteSalary(item.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="salary-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="salary-modal-title">
                Add Salary
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={closeModal}
              >
               X
              </button>
            </div>
            <form onSubmit={saveSalary}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Employee name</label>
                  <select
                    className="form-select"
                    name="employee_name"
                    value={salary.employee_name}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select employee</option>
                    {employeeNames.length === 0 ? (
                      <option value="" disabled>
                        No employee names available
                      </option>
                    ) : (
                      employeeNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Salary date</label>
                  <input
                    className="form-control"
                    type="date"
                    name="salary_date"
                    value={salary.salary_date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    min="0.01"
                    name="amount"
                    value={salary.amount}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Total salary target (optional)</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    min="0"
                    name="salary_target"
                    value={salary.salary_target}
                    onChange={handleChange}
                    placeholder="Enter full salary amount for this employee"
                  />
                  <div className="form-text">
                    Use this to calculate how much salary remains.
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={salary.notes}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Month, reference, etc."
                  />
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Salary
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showTargetModal && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowTargetModal(false);
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="target-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="target-modal-title">
                Salary target for {filterEmployee || "employee"}
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={() => setShowTargetModal(false)}
              >
                X
              </button>
            </div>
            <form onSubmit={saveSalaryTargetForm}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Total salary amount</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    min="0"
                    value={targetAmount}
                    onChange={(e) => setTargetAmount(e.target.value)}
                    placeholder="Enter full salary amount"
                    autoFocus
                  />
                  <div className="form-text">
                    Leave blank to clear the target for this employee.
                  </div>
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowTargetModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save target
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
